import { ComponentProps, useEffect } from 'react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  ColorPicker,
  NakedButton,
  PopoverDialog,
  TextField,
  useDebouncedValue,
  usePopoverDialog,
} from '@frontend/design-system';
import { COLOR_PICKER_PALETTE } from '../constants';
import { Setting } from './setting';

type Props = Omit<ComponentProps<typeof TextField>, 'label'> & {
  label?: string;
};

export const ColorSetting = ({ label, ...fieldProps }: Props) => {
  const { t } = useTranslation('content-composer');
  const debouncedValue = useDebouncedValue(fieldProps.value, 500);

  useEffect(() => {
    if (fieldProps.value) {
      const validatedColor = CSS.supports('color', fieldProps.value)
        ? fieldProps.value
        : CSS.supports('color', `#${fieldProps.value}`)
        ? `#${fieldProps.value}`
        : '';
      // @ts-ignore it's the wrong type in the design system
      fieldProps.onChange({ value: validatedColor.toUpperCase() });
    }
  }, [fieldProps.value]);

  const handleColorChange = (color: string) => fieldProps.onChange({ ...fieldProps, value: color.toUpperCase() });

  const { close, getDialogProps, getTriggerProps } = usePopoverDialog({
    placement: 'bottom-end',
    initialOffset: {
      x: -110,
      y: 32,
    },
  });

  return (
    <Setting.Item label={label ?? t('Color')}>
      <PopoverDialog
        {...getDialogProps()}
        css={{
          borderRadius: theme.borderRadius.small,
        }}
      >
        <ColorPicker
          initialColor={debouncedValue}
          onChange={handleColorChange}
          onClick={close}
          palette={COLOR_PICKER_PALETTE}
        />
      </PopoverDialog>
      <TextField
        {...fieldProps}
        label=''
        css={{
          alignItems: 'stretch',
          border: `1px solid ${theme.colors.neutral30}`,
          height: theme.spacing(4),
          padding: theme.spacing(0, 0, 0, 1),
          width: theme.spacing(14),

          '&:focus-within': {
            borderColor: theme.colors.primary50,
          },

          '& input': {
            flex: 1,
            padding: theme.spacing(0, 1),
            marginLeft: '0 !important',
          },
        }}
        startAdornment={
          <NakedButton
            css={{
              alignSelf: 'center',
              background: fieldProps.value,
              border: `1px solid ${theme.colors.neutral50}`,
              borderRadius: theme.borderRadius.small,
              height: theme.spacing(2),
              width: theme.spacing(2),
            }}
            {...getTriggerProps()}
          />
        }
        onChange={(e) => {
          // @ts-ignore it's the wrong type in the design system
          handleColorChange(e.target.value);
        }}
      />
    </Setting.Item>
  );
};
